import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthProvider } from '@/auth/AuthProvider'
import RequireAuth from '@/auth/RequireAuth'
import AppLayout from '@/components/AppLayout'
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'

/**
 * Alles hinter dem Login wird nachgeladen.
 *
 * Login und Registrierung bleiben im ersten Bündel — sie sind das Erste, was
 * jeder sieht. Der Rest, allen voran die Diagramm-Bibliothek, wird erst geholt,
 * wenn die Seite tatsächlich aufgerufen wird. Über eine Heim-Internetleitung
 * macht das den Unterschied zwischen einem und mehreren Sekunden bis zum
 * Anmeldeformular.
 */
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const BrewListPage = lazy(() => import('@/pages/brews/BrewListPage'))
const BrewNewPage = lazy(() => import('@/pages/brews/BrewNewPage'))
const BrewDetailPage = lazy(() => import('@/pages/brews/BrewDetailPage'))
const StatsPage = lazy(() => import('@/pages/StatsPage'))
const FeedPage = lazy(() => import('@/pages/FeedPage'))
const GrindersPage = lazy(() => import('@/pages/stammdaten/GrindersPage'))
const MachinesPage = lazy(() => import('@/pages/stammdaten/MachinesPage'))
const BrewersPage = lazy(() => import('@/pages/stammdaten/BrewersPage'))
const BeansPage = lazy(() => import('@/pages/stammdaten/BeansPage'))
const WatersPage = lazy(() => import('@/pages/stammdaten/WatersPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))

function PageFallback() {
  const { t } = useTranslation()
  return <p className="text-roast-500 dark:text-crema-300/70 text-sm">{t('common.loading')}</p>
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<PageFallback />}>
                  <DashboardPage />
                </Suspense>
              }
            />
            <Route
              path="/brews"
              element={
                <Suspense fallback={<PageFallback />}>
                  <BrewListPage />
                </Suspense>
              }
            />
            <Route
              path="/brews/neu"
              element={
                <Suspense fallback={<PageFallback />}>
                  <BrewNewPage />
                </Suspense>
              }
            />
            <Route
              path="/brews/:id"
              element={
                <Suspense fallback={<PageFallback />}>
                  <BrewDetailPage />
                </Suspense>
              }
            />
            <Route
              path="/auswertung"
              element={
                <Suspense fallback={<PageFallback />}>
                  <StatsPage />
                </Suspense>
              }
            />
            <Route
              path="/geteilt"
              element={
                <Suspense fallback={<PageFallback />}>
                  <FeedPage />
                </Suspense>
              }
            />
            <Route
              path="/muehlen"
              element={
                <Suspense fallback={<PageFallback />}>
                  <GrindersPage />
                </Suspense>
              }
            />
            <Route
              path="/maschinen"
              element={
                <Suspense fallback={<PageFallback />}>
                  <MachinesPage />
                </Suspense>
              }
            />
            <Route
              path="/filter"
              element={
                <Suspense fallback={<PageFallback />}>
                  <BrewersPage />
                </Suspense>
              }
            />
            <Route
              path="/bohnen"
              element={
                <Suspense fallback={<PageFallback />}>
                  <BeansPage />
                </Suspense>
              }
            />
            <Route
              path="/wasser"
              element={
                <Suspense fallback={<PageFallback />}>
                  <WatersPage />
                </Suspense>
              }
            />
            <Route
              path="/einstellungen"
              element={
                <Suspense fallback={<PageFallback />}>
                  <SettingsPage />
                </Suspense>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
